import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { MessageSquare, Send, Power, AlertTriangle, CheckCircle2, ShieldCheck, FileText, History, Star, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_META = {
  schedule_change:        { label: 'Schedule changes',              help: 'Employees get an SMS when their schedule is added, edited, or cancelled.' },
  job_assignment:         { label: 'New job assignment',            help: 'Tech receives an SMS when a new job is assigned to them.' },
  verification_code:      { label: 'Verification codes (OTP)',      help: 'Account sign-up and security re-verification codes.' },
  appointment_confirmation: { label: 'Appointment confirmation',    help: 'Customer SMS when an appointment is scheduled / published.' },
  tech_en_route:          { label: 'Tech en route',                 help: 'Customer SMS when a technician is dispatched / on the way.' },
  portal_invite:          { label: 'Portal invitation',             help: 'SMS the customer portal invite link alongside the email invite.' },
  google_review_request:  { label: 'Google review request',         help: 'Sent ~1 hour after a satisfied job is completed.' },
  membership_renewal:     { label: 'Membership renewal reminders',  help: 'Customer membership renewal & expiry notices.' },
  payment_reminder:       { label: 'Payment reminders',              help: 'Overdue / upcoming invoice nudges.' },
  dispatch_update:        { label: 'Dispatch updates',               help: 'On-the-way + arrival ETA messages to customers.' },
  manual_test:            { label: 'Manual test sends',              help: 'Admin "Send Test SMS" from this page.' },
};

const AdminSmsSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testForm, setTestForm] = useState({ phone: '', message: '' });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [consents, setConsents] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingConsents, setLoadingConsents] = useState(false);
  const [reviewLinkDraft, setReviewLinkDraft] = useState('');
  const [savingReviewLink, setSavingReviewLink] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sms/settings`, { withCredentials: true });
      setSettings(data);
      setReviewLinkDraft(data.google_review_link || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load SMS settings');
    }
    setLoading(false);
  };

  const saveReviewLink = async (reset = false) => {
    setSavingReviewLink(true);
    try {
      const { data } = await axios.put(`${API}/admin/sms/google-review-link`,
        reset ? { reset: true } : { url: reviewLinkDraft.trim() },
        { withCredentials: true });
      setSettings((prev) => ({ ...prev, google_review_link: data.url }));
      setReviewLinkDraft(data.url || '');
      toast.success(data.is_default ? 'Reset to default review link' : 'Google review link saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save review link');
    }
    setSavingReviewLink(false);
  };

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const { data } = await axios.get(`${API}/admin/sms/log?limit=100`, { withCredentials: true });
      setLogs(data || []);
    } catch (e) {
      toast.error('Failed to load SMS log');
    }
    setLoadingLogs(false);
  };

  const loadConsents = async () => {
    setLoadingConsents(true);
    try {
      const { data } = await axios.get(`${API}/admin/sms/consents`, { withCredentials: true });
      setConsents(data || []);
    } catch (e) {
      toast.error('Failed to load consents');
    }
    setLoadingConsents(false);
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/sms/settings`,
        { enabled: settings.enabled, events: settings.events },
        { withCredentials: true });
      setSettings((prev) => ({ ...prev, enabled: data.enabled, events: data.events }));
      toast.success('SMS settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const sendTest = async (e) => {
    e.preventDefault();
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/sms/test`, testForm, { withCredentials: true });
      setTestResult(data);
      if (data.sent) toast.success(`SMS sent (SID ${data.sid})`);
      else if (data.fallback) toast.warning('Twilio not configured — message recorded in fallback log.');
      else toast.error(data.error || data.reason || 'Send failed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    }
    setTesting(false);
  };

  if (loading || !settings) {
    return <div className="text-[#A3A3A3]">Loading…</div>;
  }

  const updateEvent = (k, v) => {
    setSettings((prev) => ({ ...prev, events: { ...prev.events, [k]: v } }));
  };

  return (
    <div data-testid="admin-sms-settings" className="space-y-8">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-[#D4AF37]" /> SMS / Twilio
        </h2>
        <p className="text-[#A3A3A3]">Live Twilio status, kill-switch, per-event toggles, manual test sends, delivery history, and opt-in records.</p>
      </div>

      {/* Status strip */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="bg-[#141414] border border-[#262626] rounded-md p-4">
          <p className="text-[#525252] text-xs uppercase">Twilio Connectivity</p>
          <div className="mt-2 flex items-center gap-2">
            {settings.twilio_configured ? (
              <Badge data-testid="twilio-status" className="bg-green-500/15 text-green-400 border-green-700"><CheckCircle2 className="w-3 h-3 mr-1" /> Configured</Badge>
            ) : (
              <Badge data-testid="twilio-status" className="bg-amber-500/15 text-amber-400 border-amber-700"><AlertTriangle className="w-3 h-3 mr-1" /> Not configured</Badge>
            )}
          </div>
          <p className="text-[#525252] text-xs mt-2">From: <span className="text-white font-mono">{settings.twilio_from || '—'}</span></p>
          <p className="text-[#525252] text-[10px] mt-1">Set Account SID / Auth Token / From Number under <span className="text-[#A3A3A3]">Settings → API Keys → Twilio</span>.</p>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded-md p-4">
          <p className="text-[#525252] text-xs uppercase">Global SMS Kill Switch</p>
          <div className="mt-3 flex items-center gap-3">
            <Switch data-testid="sms-global-enabled" checked={!!settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} />
            <span className="text-white text-sm">{settings.enabled ? 'SMS ENABLED' : 'SMS DISABLED (emergency stop)'}</span>
          </div>
          <p className="text-[#525252] text-[10px] mt-2">Disables all outbound SMS (incl. OTPs) until re-enabled.</p>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded-md p-4 flex flex-col">
          <p className="text-[#525252] text-xs uppercase">Compliance Pages</p>
          <ul className="text-sm space-y-1 mt-2">
            <li><a href="/sms-opt-in" target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:text-[#F3E5AB] flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Public Opt-In page</a></li>
            <li><a href="/terms" target="_blank" rel="noreferrer" className="text-[#A3A3A3] hover:text-white">Terms of Service</a></li>
            <li><a href="/privacy" target="_blank" rel="noreferrer" className="text-[#A3A3A3] hover:text-white">Privacy Policy</a></li>
          </ul>
        </div>
      </div>

      <Tabs defaultValue="events" className="w-full">
        <TabsList data-testid="sms-tabs" className="bg-[#141414] border border-[#262626]">
          <TabsTrigger value="events" data-testid="tab-events" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white"><Power className="w-4 h-4 mr-1" /> Event Toggles</TabsTrigger>
          <TabsTrigger value="test" data-testid="tab-test" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white"><Send className="w-4 h-4 mr-1" /> Send Test</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log" onClick={loadLogs} className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white"><History className="w-4 h-4 mr-1" /> Delivery Log</TabsTrigger>
          <TabsTrigger value="consents" data-testid="tab-consents" onClick={loadConsents} className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white"><ShieldCheck className="w-4 h-4 mr-1" /> Opt-In Records</TabsTrigger>
        </TabsList>

        {/* Events */}
        <TabsContent value="events" className="mt-6">
          <div className="bg-[#141414] border border-[#262626] rounded-md divide-y divide-[#262626]">
            {Object.entries(EVENT_META).map(([k, meta]) => {
              const on = !!settings.events?.[k];
              return (
                <div key={k} className="flex items-center justify-between p-4 gap-4 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-white">{meta.label}</p>
                    <p className="text-[#A3A3A3] text-xs">{meta.help}</p>
                  </div>
                  <Switch data-testid={`sms-event-${k}`} checked={on} onCheckedChange={(v) => updateEvent(k, v)} />
                </div>
              );
            })}
          </div>
          <Button data-testid="save-sms-settings" onClick={save} disabled={saving} className="mt-4 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
            {saving ? 'Saving…' : 'Save SMS Settings'}
          </Button>
          <p className="text-[#525252] text-xs mt-3">Initial rollout default: only <span className="text-white">Schedule Changes</span>, <span className="text-white">Verification Codes</span>, and <span className="text-white">Manual Test</span> are on. Turn the rest on after field testing.</p>

          {/* Google Review Link manager */}
          <div data-testid="google-review-link-card" className="mt-8 bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3 max-w-2xl">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="font-['Outfit'] text-lg font-semibold text-white">Google Review Link</h3>
            </div>
            <p className="text-[#A3A3A3] text-xs">
              Used in the post-job review-request SMS sent ~1 hour after a satisfied completion. Update this whenever your Google Business Profile review URL changes.
            </p>
            <div>
              <Label className="text-white text-sm">Review URL</Label>
              <Input
                data-testid="google-review-link-input"
                type="url"
                value={reviewLinkDraft}
                onChange={(e) => setReviewLinkDraft(e.target.value)}
                placeholder="https://g.page/r/.../review"
                className="bg-[#0A0A0A] border-[#262626] text-white h-11 font-mono text-sm"
              />
              <p className="text-[#525252] text-[10px] mt-1">
                Currently in use: <span className="text-white font-mono">{settings.google_review_link || '—'}</span>
                {settings.google_review_link_default && settings.google_review_link === settings.google_review_link_default && (
                  <span className="ml-2 text-[#A3A3A3]">(default)</span>
                )}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                data-testid="save-google-review-link"
                onClick={() => saveReviewLink(false)}
                disabled={savingReviewLink || !reviewLinkDraft.trim() || reviewLinkDraft.trim() === (settings.google_review_link || '')}
                className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
              >
                {savingReviewLink ? 'Saving…' : 'Save Review Link'}
              </Button>
              <Button
                data-testid="reset-google-review-link"
                onClick={() => saveReviewLink(true)}
                disabled={savingReviewLink}
                variant="outline"
                className="bg-transparent border-[#262626] text-[#A3A3A3] hover:bg-[#1f1f1f] hover:text-white"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" /> Reset to default
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Send Test */}
        <TabsContent value="test" className="mt-6">
          <form onSubmit={sendTest} className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3 max-w-lg">
            <div>
              <Label className="text-white">Phone</Label>
              <Input data-testid="test-sms-phone" type="tel" required value={testForm.phone} onChange={(e) => setTestForm({ ...testForm, phone: e.target.value })} placeholder="(614) 555-1234 or +16145551234" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
              <p className="text-[#525252] text-xs mt-1">Auto-normalized to E.164. US numbers default to +1.</p>
            </div>
            <div>
              <Label className="text-white">Custom Message (optional)</Label>
              <Input data-testid="test-sms-message" value={testForm.message} onChange={(e) => setTestForm({ ...testForm, message: e.target.value })} placeholder="Leaves default if blank" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
            </div>
            <Button data-testid="send-test-sms" type="submit" disabled={testing} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
              <Send className="w-4 h-4 mr-2" /> {testing ? 'Sending…' : 'Send Test SMS'}
            </Button>
            {testResult && (
              <div data-testid="test-sms-result" className={`text-sm p-3 rounded-md ${testResult.sent ? 'bg-green-500/10 text-green-400 border border-green-700' : testResult.fallback ? 'bg-amber-500/10 text-amber-400 border border-amber-700' : 'bg-red-500/10 text-red-400 border border-red-700'}`}>
                <p>To: <span className="font-mono">{testResult.to}</span></p>
                {testResult.sent && <p>Sent! SID <span className="font-mono">{testResult.sid}</span> · status <span className="font-mono">{testResult.status}</span></p>}
                {testResult.fallback && <p>Fallback mode — Twilio not configured. Add credentials under Settings → API Keys.</p>}
                {!testResult.sent && !testResult.fallback && <p>Failed: {testResult.error || testResult.reason}</p>}
              </div>
            )}
          </form>
        </TabsContent>

        {/* Delivery log */}
        <TabsContent value="log" className="mt-6">
          <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
            {loadingLogs ? (
              <p className="p-4 text-[#A3A3A3]">Loading…</p>
            ) : logs.length === 0 ? (
              <p className="p-6 text-center text-[#A3A3A3]">No SMS log entries yet.</p>
            ) : (
              <table className="w-full min-w-[700px] text-sm">
                <thead className="bg-[#0A0A0A] text-[#A3A3A3] text-xs uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">To</th>
                    <th className="text-left px-3 py-2">Event</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">SID</th>
                    <th className="text-left px-3 py-2">Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-t border-[#262626]">
                      <td className="px-3 py-2 text-[#A3A3A3]">{l.created_at ? new Date(l.created_at).toLocaleString() : ''}</td>
                      <td className="px-3 py-2 text-white font-mono text-xs">{l.to}</td>
                      <td className="px-3 py-2 text-[#A3A3A3]">{l.event}</td>
                      <td className="px-3 py-2">
                        {l.sent ? (
                          <Badge className="bg-green-500/15 text-green-400 border-green-700">{l.status || 'queued'}</Badge>
                        ) : (
                          <Badge className="bg-amber-500/15 text-amber-400 border-amber-700">{l.status || 'failed'}</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[#525252] font-mono text-xs">{l.sid || '—'}</td>
                      <td className="px-3 py-2 text-[#A3A3A3] max-w-xs truncate">{l.message_preview}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* Consents */}
        <TabsContent value="consents" className="mt-6">
          <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
            {loadingConsents ? (
              <p className="p-4 text-[#A3A3A3]">Loading…</p>
            ) : consents.length === 0 ? (
              <p className="p-6 text-center text-[#A3A3A3]">No public opt-in records yet. Share <a className="text-[#D4AF37]" href="/sms-opt-in">/sms-opt-in</a> with users.</p>
            ) : (
              <table className="w-full min-w-[700px] text-sm">
                <thead className="bg-[#0A0A0A] text-[#A3A3A3] text-xs uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Email</th>
                    <th className="text-left px-3 py-2">Phone</th>
                    <th className="text-left px-3 py-2">IP</th>
                    <th className="text-left px-3 py-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {consents.map((c) => (
                    <tr key={c.id} className="border-t border-[#262626]">
                      <td className="px-3 py-2 text-[#A3A3A3]">{c.consent_at ? new Date(c.consent_at).toLocaleString() : ''}</td>
                      <td className="px-3 py-2 text-white">{c.full_name}</td>
                      <td className="px-3 py-2 text-[#A3A3A3]">{c.email}</td>
                      <td className="px-3 py-2 text-white font-mono text-xs">{c.phone}</td>
                      <td className="px-3 py-2 text-[#525252] font-mono text-xs">{c.ip || '—'}</td>
                      <td className="px-3 py-2 text-[#525252]">{c.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSmsSettings;
